import { RiggedCharacterRef } from '../../types/typing.ts';
import { syncRiggedActionPose } from '../canvas/RiggedCharacterRenderer';

class RiggedControl {
  cacheMap: Record<string, RiggedCharacterRef> = {};

  enter(data: {
    id: string;
    src: string;
    startTime: number;
    endTime: number;
    time: number;
    characterRef: any; // Using any to avoid complex map typing, but it is RefObject<Map<string, RiggedCharacterRef>>
  }) {
    const { id, characterRef, startTime } = data;
    let item: RiggedCharacterRef | undefined;

    if (this.cacheMap[id]) {
      item = this.cacheMap[id];
    } else if (characterRef && characterRef.current) {
      // Handle Map
      if (characterRef.current instanceof Map) {
        item = characterRef.current.get(id);
      } else {
        // Fallback if not Map (though it should be)
        console.warn(
          'RiggedControl: characterRef is not a Map',
          characterRef.current
        );
      }

      if (item) {
        this.cacheMap[id] = item;
      }
    }

    if (item && item.container) {
      item.startTime = startTime;
      item.container.visible = true;
    }
  }

  update(data: {
    id: string;
    src: string;
    startTime: number;
    endTime: number;
    time: number;
    characterRef: any;
  }) {
    const { id, startTime, endTime, time, characterRef } = data;

    // Ensure we have the item
    let item = this.cacheMap[id];
    if (!item && characterRef?.current?.get) {
      item = characterRef.current.get(id);
      if (item) this.cacheMap[id] = item;
    }

    if (!item || !item.container) return;

    // Update start time to account for timeline block dragging
    item.startTime = startTime;

    // Make sure container is visible during playback
    if (!item.container.visible) item.container.visible = true;

    // Handle position-based path animations (template tweening)
    if (item.pathPoints && item.pathPoints.length > 0) {
      // Calculate progress ratio based on current time
      const duration = endTime - startTime;
      const currentTime = time - startTime;
      const progress = Math.max(0, Math.min(1, currentTime / duration));

      // Calculate total path length for even distribution
      const totalPoints = item.pathPoints.length;

      // Calculate which segment we should be on
      const currentSegmentIndex = Math.floor(progress * (totalPoints - 1));
      const nextSegmentIndex = Math.min(
        currentSegmentIndex + 1,
        totalPoints - 1
      );

      // Calculate sub-progress within current segment
      const segmentProgress = (progress * (totalPoints - 1)) % 1;

      // Get current and next points
      const currentPoint = item.pathPoints[currentSegmentIndex];
      const nextPoint = item.pathPoints[nextSegmentIndex];

      // Interpolate position
      const currentX =
        currentPoint.x + (nextPoint.x - currentPoint.x) * segmentProgress;
      const currentY =
        currentPoint.y + (nextPoint.y - currentPoint.y) * segmentProgress;

      // Update container position
      item.container.position.x = currentX;
      item.container.position.y = currentY;
    }

    // Sync the GSAP animation timeline with global timeline (bone animations)
    if (item.timeline) {
      // Calculate the local animation time within this action block
      const actionDuration = endTime - startTime;
      const localTime = time - startTime;

      // Calculate what fraction through the action we are
      const progress = Math.max(0, Math.min(1, localTime / actionDuration));

      // Get the total duration of the GSAP timeline
      const gsapDuration = item.timeline.duration();

      // Seek to the appropriate position
      const seekTime = progress * gsapDuration;
      item.timeline.seek(seekTime);

      if (item.segmentSprites && item.segmentSprites.size > 0) {
        syncRiggedActionPose(item, progress);
      }

      // Pause the timeline so it doesn't auto-advance
      // (the global timeline will call update to move it)
      if (item.timeline.isActive()) {
        item.timeline.pause();
      }
    }
  }

  leave(data: {
    id: string;
    startTime: number;
    endTime: number;
    time: number;
    characterRef: any;
  }) {
    const { id, time, startTime, endTime } = data;

    // Ensure we have the item
    let item = this.cacheMap[id];
    if (!item && data.characterRef?.current?.get) {
      item = data.characterRef.current.get(id);
      if (item) this.cacheMap[id] = item;
    }

    if (!item) return;

    if (time < startTime || time > endTime) {
      if (item.container) item.container.visible = false;
    } else {
      if (item.container) item.container.visible = true;
    }
  }

  destroy() {
    this.cacheMap = {};
  }
}

export default new RiggedControl();
